import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { subDays, startOfDay, startOfMonth, format } from 'date-fns';
import AnalyticsService from '../../services/AnalyticsService';
import ExportService from '../../services/ExportService';
import DateRangePicker from '../../components/DateRangePicker';
import { AnalyticsFilters, FilteredAnalytics } from '../../types';

const DashboardPage: React.FC = () => {
  // Filter state (UI state - changes as user types/selects)
  const [filters, setFilters] = useState<AnalyticsFilters>({
    model: 'all',
    dateRange: {
      start: subDays(new Date(), 30),
      end: new Date(),
    },
  });

  // Applied filters (trigger data fetching)
  const [appliedFilters, setAppliedFilters] =
    useState<AnalyticsFilters>(filters);

  const [analytics, setAnalytics] = useState<FilteredAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateError, setDateError] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAnalytics(appliedFilters);
  }, [appliedFilters]);

  const loadAnalytics = async (filters: AnalyticsFilters) => {
    try {
      setLoading(true);
      setError(null);
      const data = await AnalyticsService.getFilteredAnalytics(filters);
      setAnalytics(data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading analytics:', error);
      const message = error instanceof Error ? error.message : 'Unknown error';
      setError(`Failed to load analytics: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  const validateDateRange = (dateRange: {
    start: Date;
    end: Date;
  }): boolean => {
    return dateRange.start <= dateRange.end && dateRange.end <= new Date();
  };

  const handleApplyFilters = () => {
    if (validateDateRange(filters.dateRange)) {
      setAppliedFilters({ ...filters });
      setDateError('');
    } else {
      setDateError(
        'Start date must be before end date and end date cannot be in the future'
      );
    }
  };

  const handleClearFilters = () => {
    const defaultFilters: AnalyticsFilters = {
      model: 'all',
      dateRange: { start: subDays(new Date(), 30), end: new Date() },
    };
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setDateError('');
  };

  const handleQuickSelect = (period: 'today' | 'last7days' | 'thisMonth') => {
    let newDateRange: { start: Date; end: Date };

    switch (period) {
      case 'today':
        newDateRange = { start: startOfDay(new Date()), end: new Date() };
        break;
      case 'last7days':
        newDateRange = { start: subDays(new Date(), 7), end: new Date() };
        break;
      case 'thisMonth':
        newDateRange = { start: startOfMonth(new Date()), end: new Date() };
        break;
    }

    setFilters({ ...filters, dateRange: newDateRange, quickSelect: period });
  };

  const handleExport = async () => {
    if (!analytics) return;
    try {
      await ExportService.exportAnalyticsCSV(analytics.monitoringTrends);
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Failed to load analytics data</p>
      </div>
    );
  }

  // Prepare data for Monitoring Trends chart
  const monitoringTrendsData = analytics.monitoringTrends.map((item) => ({
    date: format(new Date(item.date), 'MMM dd'),
    'Total Calls': item.calls,
    'Deepfake Calls': item.deepfakes,
  }));

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Analytics Dashboard
          </h1>
          <div className="flex items-center gap-4 mt-1">
            <p className="text-gray-600">System-wide overview and analytics</p>
            {lastUpdated && (
              <p className="text-sm text-gray-500">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          <span>Export Data</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-red-600 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Empty State Info */}
      {!error && analytics.totalCalls === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-blue-600 mr-2 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <p className="font-medium text-blue-800">No call records found</p>
              <p className="text-sm text-blue-700 mt-1">
                No call data has been synced for the selected period. This
                usually means the mobile app hasn't synced call records to the
                cloud yet. Ensure users are signed in (not in guest mode) and
                have made calls through the app.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Model Filter */}
          <div>
            <label
              htmlFor="model-filter"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Model
            </label>
            <select
              id="model-filter"
              value={filters.model}
              onChange={(e) =>
                setFilters({ ...filters, model: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Models</option>
              <option value="AASIST-L">AASIST-L</option>
              <option value="AASIST">AASIST</option>
              <option value="DistilHuBERT + MLP">DistilHuBERT + MLP</option>
            </select>
          </div>

          {/* Date Range Picker */}
          <div>
            <DateRangePicker
              startDate={filters.dateRange.start}
              endDate={filters.dateRange.end}
              onStartDateChange={(date) =>
                setFilters({
                  ...filters,
                  dateRange: { ...filters.dateRange, start: date },
                  quickSelect: undefined,
                })
              }
              onEndDateChange={(date) =>
                setFilters({
                  ...filters,
                  dateRange: { ...filters.dateRange, end: date },
                  quickSelect: undefined,
                })
              }
              error={dateError}
            />
          </div>
        </div>

        {/* Quick Select Buttons */}
        <div className="flex items-center space-x-3 mb-4">
          <span className="text-sm font-medium text-gray-700">
            Quick select:
          </span>
          <button
            onClick={() => handleQuickSelect('today')}
            className={`px-3 py-1 text-sm rounded-md transition ${
              filters.quickSelect === 'today'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Today
          </button>
          <button
            onClick={() => handleQuickSelect('last7days')}
            className={`px-3 py-1 text-sm rounded-md transition ${
              filters.quickSelect === 'last7days'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Last 7 days
          </button>
          <button
            onClick={() => handleQuickSelect('thisMonth')}
            className={`px-3 py-1 text-sm rounded-md transition ${
              filters.quickSelect === 'thisMonth'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            This month
          </button>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-3">
          <button
            onClick={handleApplyFilters}
            disabled={!!dateError}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply Filters
          </button>
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Calls */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Calls</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {analytics.totalCalls}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Legitimate Calls */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Legitimate Calls</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {analytics.legitimateCalls}
              </p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <svg
                className="w-6 h-6 text-success"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Real Hacks (Deepfakes Detected) */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-1">
            <div>
              <p className="text-gray-600 text-sm">Deepfake Calls</p>
              <p className="text-3xl font-bold text-danger mt-2">
                {analytics.deepfakeCalls}
              </p>
            </div>
            <div className="w-12 h-12 bg-danger-100 rounded-lg flex items-center justify-center">
              <svg
                className="w-6 h-6 text-danger"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            {analytics.totalCalls > 0
              ? (
                  (analytics.deepfakeCalls / analytics.totalCalls) *
                  100
                ).toFixed(1)
              : '0.0'}
            % detection rate
          </p>
        </div>

        {/* Average Confidence Score */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Avg Confidence</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {(analytics.avgConfidenceScore * 100).toFixed(1)}%
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg
                className="w-6 h-6 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6">
        {/* Monitoring Trends */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Monitoring Trends
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monitoringTrendsData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="Total Calls"
                stroke="#007AFF"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="Deepfake Calls"
                stroke="#FF3B30"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
